import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { 
    Settings, 
    Building, 
    User, 
    Shield, 
    Download, 
    MessageSquare,
    Languages,
    Save, 
    Eye, 
    EyeOff,
    Clock,
    Mail,
    Phone,
    MapPin,
    Image,
    Upload,
    Trash2,
    X,
    Info
} from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import FeedbackTab from './FeedbackTab';
import LanguageSelector from '../../i18n/LanguageSelector';

export default function GymSettings() {
    const { getAuthenticatedApi, currentUser } = useAuth();
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('gym');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    
    // Stati per i vari form
    const [gymSettings, setGymSettings] = useState({
        id: null,
        name: '',
        email: '',
        phone: '',
        address: '',
        max_users: 100,
        max_trainers: 10,
        opening_hours: '06:00',
        closing_hours: '23:00',
        description: '',
        logo_filename: null
    });
    
    // Stati per logo upload
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    const [profileSettings, setProfileSettings] = useState({
        name: currentUser?.name || '',
        email: currentUser?.email || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
    });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    useEffect(() => {
        fetchGymSettings();
    }, []);

    const fetchGymSettings = async () => {
        try {
            setLoading(true);
            const api = getAuthenticatedApi();
            const response = await api.get('/gyms.php');
            
            if (response.data.length > 0) {
                const gym = response.data[0];
                setGymSettings({
                    id: gym.id,
                    name: gym.name || '',
                    email: gym.email || '',
                    phone: gym.phone || '',
                    address: gym.address || '',
                    max_users: gym.max_users || 100,
                    max_trainers: gym.max_trainers || 10,
                    opening_hours: gym.opening_hours || '06:00',
                    closing_hours: gym.closing_hours || '23:00',
                    description: gym.description || '',
                    logo_filename: gym.logo_filename || null
                });
                
                // Imposta preview logo se esiste (con timestamp per cache busting)
                if (gym.logo_filename) {
                    const cacheBuster = Date.now();
                    setLogoPreview(`/api/serve_image.php?filename=${gym.logo_filename}&type=logo&v=${cacheBuster}`);
                }
            }
        } catch (err) {
            console.error('Errore nel caricamento impostazioni:', err);
            setMessage({ type: 'error', text: t('gym.settings.messages.loadingError') });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveGymSettings = async (e) => {
        e.preventDefault();
        setSaving(true);
        
        try {
            const api = getAuthenticatedApi();
            const response = await api.get('/gyms.php');
            const gymId = response.data[0]?.id;
            
            if (gymId) {
                const { max_users, max_trainers, ...payload } = gymSettings;
                await api.put(`/gyms.php?id=${gymId}`, payload);
                setMessage({ type: 'success', text: t('gym.settings.messages.saveSuccess') });
            }
        } catch (err) {
            console.error('Errore nel salvataggio:', err);
            setMessage({ type: 'error', text: t('gym.settings.messages.saveError') });
        } finally {
            setSaving(false);
        }
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        
        if (profileSettings.new_password !== profileSettings.confirm_password) {
            setMessage({ type: 'error', text: t('gym.settings.messages.passwordMismatch') });
            return;
        }

        setSaving(true);
        setMessage({ type: 'info', text: t('gym.settings.messages.featureInDevelopment') });
        setSaving(false);
    };
    
    const handleLogoFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Validazione tipo file
        const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            setMessage({ type: 'error', text: 'Tipo di file non supportato. Usa PNG, JPG o SVG' });
            return;
        }
        
        // Validazione dimensione (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            setMessage({ type: 'error', text: 'File troppo grande. Massimo 2MB' });
            return;
        }
        
        setLogoFile(file);
        
        // Crea preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };
    
    const handleLogoUpload = async () => {
        if (!logoFile || !gymSettings.id) return;
        
        try {
            setUploadingLogo(true);
            const api = getAuthenticatedApi();
            
            const formData = new FormData();
            formData.append('logo', logoFile);
            
            const response = await api.post(
                `gym_logo_upload.php?gym_id=${gymSettings.id}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            
            if (response.data.filename) {
                setGymSettings(prev => ({ ...prev, logo_filename: response.data.filename }));
                setLogoFile(null);
                setMessage({ type: 'success', text: 'Logo caricato con successo!' });
                
                // Aggiorna preview con URL server + timestamp per invalidare cache
                const timestamp = response.data.timestamp || Date.now();
                setLogoPreview(`/api/serve_image.php?filename=${response.data.filename}&type=logo&v=${timestamp}`);
            }
        } catch (err) {
            console.error('Errore upload logo:', err);
            setMessage({ 
                type: 'error', 
                text: err.response?.data?.error || 'Errore nel caricamento del logo' 
            });
        } finally {
            setUploadingLogo(false);
        }
    };
    
    const handleLogoDelete = async () => {
        if (!gymSettings.id || !gymSettings.logo_filename) return;
        
        if (!window.confirm('Sei sicuro di voler eliminare il logo?')) return;
        
        try {
            setUploadingLogo(true);
            const api = getAuthenticatedApi();
            
            await api.delete(`gym_logo_upload.php?gym_id=${gymSettings.id}`);
            
            setGymSettings(prev => ({ ...prev, logo_filename: null }));
            setLogoPreview(null);
            setLogoFile(null);
            setMessage({ type: 'success', text: 'Logo eliminato con successo' });
        } catch (err) {
            console.error('Errore eliminazione logo:', err);
            setMessage({ 
                type: 'error', 
                text: err.response?.data?.error || 'Errore nell\'eliminazione del logo' 
            });
        } finally {
            setUploadingLogo(false);
        }
    };
    
    const handleCancelLogoPreview = () => {
        setLogoFile(null);
        // Ripristina preview originale se esiste
        if (gymSettings.logo_filename) {
            setLogoPreview(`/api/serve_image.php?filename=${gymSettings.logo_filename}&type=logo`);
        } else {
            setLogoPreview(null);
        }
    };

    const tabs = [
        { id: 'gym', name: t('gym.settings.tabs.gym'), icon: <Building size={20} /> },
        { id: 'profile', name: t('gym.settings.tabs.profile'), icon: <User size={20} /> },
        { id: 'security', name: t('gym.settings.tabs.security'), icon: <Shield size={20} /> },
        { id: 'data', name: t('gym.settings.tabs.data'), icon: <Download size={20} /> },
        { id: 'language', name: t('gym.navigation.language', 'Lingua'), icon: <Languages size={20} /> },
        { id: 'feedback', name: t('gym.navigation.feedback', 'Feedback'), icon: <MessageSquare size={20} /> },
    ];

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="h-full min-h-0 w-full bg-gray-50 dark:bg-gray-950 overflow-hidden">
            <div className="h-full min-h-0 w-full px-4 lg:px-6 py-4 flex flex-col gap-4">
                {/* Header */}
                <div className="shrink-0">
                    <div className="flex items-center gap-3 mb-2">
                        <Settings className="text-gray-600" size={32} />
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                            {t('gym.settings.title')}
                        </h1>
                    </div>
                    <p className="text-gray-600 dark:text-white/85">
                        {t('gym.settings.description')}
                    </p>
                </div>

                {/* Message */}
                {message && (
                    <div className={`shrink-0 p-4 rounded-lg ${
                        message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                        message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
                        'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                        {message.text}
                    </div>
                )}

                <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-4 lg:gap-6 overflow-hidden">
                    {/* Sidebar Tabs */}
                    <div className="lg:w-72 shrink-0">
                        <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-3 overflow-hidden">
                            <nav className="space-y-1">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                                            activeTab === tab.id
                                                ? 'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                                                : 'text-gray-700 dark:text-white/85 hover:bg-gray-50 dark:hover:bg-gray-800'
                                        }`}
                                    >
                                        {tab.icon}
                                        {tab.name}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
                        <div className="h-full min-h-0 bg-white dark:bg-gray-900 rounded-lg shadow overflow-hidden">
                            {activeTab === 'gym' && (
                                <GymSettingsTab
                                    settings={gymSettings}
                                    setSettings={setGymSettings}
                                    onSave={handleSaveGymSettings}
                                    saving={saving}
                                    logoPreview={logoPreview}
                                    logoFile={logoFile}
                                    uploadingLogo={uploadingLogo}
                                    onLogoFileChange={handleLogoFileChange}
                                    onLogoUpload={handleLogoUpload}
                                    onLogoDelete={handleLogoDelete}
                                    onCancelLogoPreview={handleCancelLogoPreview}
                                />
                            )}
                            
                            {activeTab === 'profile' && (
                                <ProfileSettingsTab
                                    settings={profileSettings}
                                    setSettings={setProfileSettings}
                                    onSave={handleSaveProfile}
                                    saving={saving}
                                    showPasswords={showPasswords}
                                    setShowPasswords={setShowPasswords}
                                />
                            )}
                            
                            {activeTab === 'security' && <SecurityTab />}
                            {activeTab === 'data' && <DataTab />}
                            {activeTab === 'language' && (
                                <div className="p-6">
                                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                                        Lingua
                                    </h2>
                                    <div className="max-w-sm">
                                        <LanguageSelector />
                                    </div>
                                </div>
                            )}
                            {activeTab === 'feedback' && (
                                <div className="h-full min-h-0 overflow-y-auto">
                                    <FeedbackTab />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Tab Impostazioni Palestra
function GymSettingsTab({ 
    settings, 
    setSettings, 
    onSave, 
    saving,
    logoPreview,
    logoFile,
    uploadingLogo,
    onLogoFileChange,
    onLogoUpload,
    onLogoDelete,
    onCancelLogoPreview
}) {
    const { t } = useTranslation();
    const fileInputRef = React.useRef(null);
    
    return (
        <form onSubmit={onSave} className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {t('gym.settings.gym.title')}
            </h2>
            
            {/* Logo Upload Section */}
            <div className="mb-8 p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-white/10">
                <div className="flex items-start gap-6">
                    {/* Preview Logo */}
                    <div className="flex-shrink-0">
                        {logoPreview ? (
                            <div className="relative">
                                <img 
                                    src={logoPreview} 
                                    alt="Logo Preview" 
                                    className="w-32 h-32 object-contain rounded-lg border border-gray-300 dark:border-white/10 bg-white"
                                />
                                {logoFile && (
                                    <button
                                        type="button"
                                        onClick={onCancelLogoPreview}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    >
                                        <X size={16} />
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="w-32 h-32 flex items-center justify-center bg-gray-200 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-white/10">
                                <Image size={40} className="text-gray-400" />
                            </div>
                        )}
                    </div>
                    
                    {/* Upload Controls */}
                    <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Logo Palestra
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-white/85 mb-4">
                            Carica il logo della tua palestra. Formati supportati: PNG, JPG, SVG. <br/>
                            Dimensioni: min 100x100px, max 2000x2000px. Peso massimo: 2MB.
                        </p>
                        
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml"
                            onChange={onLogoFileChange}
                            className="hidden"
                        />
                        
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingLogo}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 transition-colors"
                            >
                                <Upload size={18} />
                                Scegli File
                            </button>
                            
                            {logoFile && (
                                <button
                                    type="button"
                                    onClick={onLogoUpload}
                                    disabled={uploadingLogo}
                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 transition-colors"
                                >
                                    {uploadingLogo ? <LoadingSpinner size="small" /> : <Save size={18} />}
                                    {uploadingLogo ? 'Caricamento...' : 'Carica Logo'}
                                </button>
                            )}
                            
                            {settings.logo_filename && !logoFile && (
                                <button
                                    type="button"
                                    onClick={onLogoDelete}
                                    disabled={uploadingLogo}
                                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 transition-colors"
                                >
                                    <Trash2 size={18} />
                                    Elimina Logo
                                </button>
                            )}
                        </div>
                        
                        {logoFile && (
                            <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                                ✓ File selezionato: {logoFile.name} ({(logoFile.size / 1024).toFixed(1)} KB)
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                        {t('gym.settings.gym.fields.name')}
                    </label>
                    <input
                        type="text"
                        value={settings.name}
                        onChange={(e) => setSettings(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                        {t('gym.settings.gym.fields.email')}
                    </label>
                    <input
                        type="email"
                        value={settings.email}
                        onChange={(e) => setSettings(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                        {t('gym.settings.gym.fields.phone')}
                    </label>
                    <input
                        type="tel"
                        value={settings.phone}
                        onChange={(e) => setSettings(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                        {t('gym.settings.gym.fields.address')}
                    </label>
                    <input
                        type="text"
                        value={settings.address}
                        onChange={(e) => setSettings(prev => ({ ...prev, address: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                        {t('gym.settings.gym.fields.maxUsers')}
                    </label>
                    <input
                        type="number"
                        min="1"
                        value={settings.max_users}
                        readOnly
                        disabled
                        title={t('gym.settings.gym.fields.limitsManagedBySupport')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-md bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-white/85 cursor-not-allowed"
                        aria-describedby="max-users-support-note"
                    />
                    <p
                        id="max-users-support-note"
                        className="mt-1 text-xs text-gray-500 dark:text-white/65 flex items-center gap-2"
                    >
                        <Info className="h-4 w-4 flex-shrink-0 text-blue-500 dark:text-blue-300" />
                        {t('gym.settings.gym.fields.limitsManagedBySupport')}
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                        {t('gym.settings.gym.fields.maxTrainers')}
                    </label>
                    <input
                        type="number"
                        min="1"
                        value={settings.max_trainers}
                        readOnly
                        disabled
                        title={t('gym.settings.gym.fields.limitsManagedBySupport')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-md bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-white/85 cursor-not-allowed"
                        aria-describedby="max-trainers-support-note"
                    />
                    <p
                        id="max-trainers-support-note"
                        className="mt-1 text-xs text-gray-500 dark:text-white/65 flex items-center gap-2"
                    >
                        <Info className="h-4 w-4 flex-shrink-0 text-blue-500 dark:text-blue-300" />
                        {t('gym.settings.gym.fields.limitsManagedBySupport')}
                    </p>
                </div>
            </div>

            <div className="mt-6 flex justify-end">
                <button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 transition-colors"
                >
                    {saving ? <LoadingSpinner size="small" /> : <Save size={20} />}
                    {t('gym.settings.gym.saveButton')}
                </button>
            </div>
        </form>
    );
}

// Tab Profilo
function ProfileSettingsTab({ settings, setSettings, onSave, saving, showPasswords, setShowPasswords }) {
    const { t } = useTranslation();
    
    return (
        <form onSubmit={onSave} className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {t('gym.settings.profile.title')}
            </h2>

            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                            {t('gym.settings.profile.fields.name')}
                        </label>
                        <input
                            type="text"
                            value={settings.name}
                            onChange={(e) => setSettings(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                            {t('gym.settings.profile.fields.email')}
                        </label>
                        <input
                            type="email"
                            value={settings.email}
                            onChange={(e) => setSettings(prev => ({ ...prev, email: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                        />
                    </div>
                </div>

                <hr className="border-gray-200 dark:border-white/10" />

                <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                        {t('gym.settings.profile.fields.changePassword')}
                    </h3>
                    
                    <div className="space-y-4">
                        <PasswordField
                            label={t('gym.settings.profile.fields.currentPassword')}
                            value={settings.current_password}
                            onChange={(e) => setSettings(prev => ({ ...prev, current_password: e.target.value }))}
                            show={showPasswords.current}
                            onToggle={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                        />
                        
                        <PasswordField
                            label={t('gym.settings.profile.fields.newPassword')}
                            value={settings.new_password}
                            onChange={(e) => setSettings(prev => ({ ...prev, new_password: e.target.value }))}
                            show={showPasswords.new}
                            onToggle={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                        />
                        
                        <PasswordField
                            label={t('gym.settings.profile.fields.confirmPassword')}
                            value={settings.confirm_password}
                            onChange={(e) => setSettings(prev => ({ ...prev, confirm_password: e.target.value }))}
                            show={showPasswords.confirm}
                            onToggle={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                        />
                    </div>
                </div>
            </div>

            <div className="mt-6 flex justify-end">
                <button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 transition-colors"
                >
                    {saving ? <LoadingSpinner size="small" /> : <Save size={20} />}
                    {t('gym.settings.profile.saveButton')}
                </button>
            </div>
        </form>
    );
}

function PasswordField({ label, value, onChange, show, onToggle }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                {label}
            </label>
            <div className="relative">
                <input
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={onChange}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                />
                <button
                    type="button"
                    onClick={onToggle}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
            </div>
        </div>
    );
}

// Tab Sicurezza (placeholder)
function SecurityTab() {
    const { t } = useTranslation();
    
    return (
        <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {t('gym.settings.security.title')}
            </h2>
            <div className="text-center py-8">
                <Shield size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">{t('gym.settings.security.inDevelopment')}</p>
            </div>
        </div>
    );
}

// Tab Dati (placeholder)
function DataTab() {
    const { t } = useTranslation();
    
    return (
        <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {t('gym.settings.data.title')}
            </h2>
            <div className="text-center py-8">
                <Download size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">{t('gym.settings.data.inDevelopment')}</p>
            </div>
        </div>
    );
}


